import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailSalesChannels, type SalesChannel } from '@/api/retailSalesChannels';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'retail.sales_channel.manage';

const CHANNEL_LABEL: Record<string, string> = {
  pos: 'Торгова точка (POS)',
  online_store: 'Інтернет-магазин',
  marketplace_rozetka: 'Маркетплейс Розетка',
  marketplace_prom: 'Маркетплейс Prom',
  other: 'Інше',
};

export default function SalesChannelsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-sales-channels', orgId],
    queryFn: () => retailSalesChannels.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<SalesChannel>[]>(
    () => [
      { key: 'code', header: 'Код', width: 150, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'channel_type',
        header: 'Тип каналу',
        width: 200,
        sortValue: (r) => CHANNEL_LABEL[r.channel_type] ?? r.channel_type,
        cell: (r) => CHANNEL_LABEL[r.channel_type] ?? r.channel_type,
      },
      {
        key: 'is_active',
        header: 'Активний',
        width: 120,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати каналами продажу (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Канали продажу
          <HintBadge
            title="Що це?"
            text={
              'Канал продажу — це джерело продажу: точка продажу (POS), інтернет-магазин, ' +
              'маркетплейс. Дозволяє розрізняти продажі для аналітики ' +
              '(наприклад «50% з Розетки, 30% з POS, 20% з власного сайту»).\n\n' +
              'Типи:\n' +
              '• Торгова точка (POS) — звичайна каса у магазині.\n' +
              '• Інтернет-магазин — власний онлайн.\n' +
              '• Маркетплейс Розетка / Prom — площадки.\n' +
              '• Iнше — будь-яке інше джерело.\n\n' +
              'Якщо у вас один магазин і одна каса — створіть один канал «POS» і не парьтесь. ' +
              'Аналітика по каналах — для тих, хто продає мультиканально.'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/sales-channels/new')}
        >
          + Новий канал
        </button>
      </div>
      <DataTable<SalesChannel>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає каналів продажу."
        onRowDoubleClick={(r) => navigate(`/retail/sales-channels/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="code"
      />
    </>
  );
}
